import {
  getTermStatusUpdatesForNewDefaults,
  getUserTerm,
  isTermDelivering,
} from 'utils/terms'

import {
  queries,
  useInvalidateListings,
  useInvalidateOrderEstimate,
  useInvalidateTermStatuses,
  useSkippedWeeks,
  useUpsertUserTermStatus,
  UserV1,
} from '@tovala/browser-apis-combinedapi'
import { useUser } from 'contexts/user'
import { useQuery, useQueryClient } from '@tanstack/react-query'

export function userTermStatusesQuery({ user }: { user: UserV1 | undefined }) {
  // The API rejects term statuses with a 400 until the user has a shipping zip code.
  return queries.termStatuses.list({
    userID: user?.shippingAddresses[0]?.zipCode ? user.id : undefined,
  })
}

export function useUserTermStatuses({ user }: { user: UserV1 }) {
  return useQuery(userTermStatusesQuery({ user }))
}

export function useSelectedUserTerm({
  selectedUserTermID,
}: {
  selectedUserTermID: number | undefined
}) {
  const { user } = useUser()

  const {
    data: termStatuses = [],
    error: loadUserTermStatusesError,
    isError: hasLoadUserTermStatusesError,
    isPending: isLoadingUserTermStatuses,
  } = useQuery({
    ...userTermStatusesQuery({ user }),
    staleTime: 0,
  })

  const { data: skippedWeeks, isPending: isLoadingSkippedWeeks } =
    useSkippedWeeks({
      userID: user.id,
    })

  const selectedTerm = termStatuses.find(
    (term) => term.termID === selectedUserTermID,
  )

  const { data: orderEstimate } = useQuery({
    ...queries.orderEstimates.detail({
      termID: selectedTerm?.termID ?? undefined,
      userID: user.id,
    }),
    enabled: !!selectedTerm && isTermDelivering(selectedTerm),
  })

  return {
    error: hasLoadUserTermStatusesError ? loadUserTermStatusesError : null,
    isPending: isLoadingUserTermStatuses || isLoadingSkippedWeeks,
    selectedUserTerm: selectedTerm
      ? getUserTerm({ orderEstimate, skippedWeeks, term: selectedTerm, user })
      : undefined,
  }
}

export function useUpdateTermStatusesToNewDefaults() {
  const queryClient = useQueryClient()
  const { user } = useUser()
  const { mutateAsync: upsertUserTermStatusAsync } = useUpsertUserTermStatus()
  const { invalidateUserTermStatuses } = useInvalidateTermStatuses()
  const { invalidateAllListingSelections } = useInvalidateListings()
  const { resetAllOrderEstimates } = useInvalidateOrderEstimate()

  const updateTermStatusesToNewDefaults = async ({
    onlyWeeksWithMaxSelections,
    shipPeriod,
    subscriptionTypeID,
  }: {
    onlyWeeksWithMaxSelections?: number
    shipPeriod: number
    subscriptionTypeID: string
  }) => {
    // Activating a plan creates week records server-side (pinning weeks with selections to the old plan), so
    // read them fresh or we'd POST weeks that now exist.
    const latestTermStatuses = await queryClient.fetchQuery({
      ...queries.termStatuses.list({ userID: user.id }),
      staleTime: 0,
    })

    try {
      await Promise.all(
        getTermStatusUpdatesForNewDefaults({
          onlyWeeksWithMaxSelections,
          shipPeriod,
          subscriptionTypeID,
          termStatuses: latestTermStatuses,
        }).map(({ subTermID, termStatus }) =>
          upsertUserTermStatusAsync({
            data: {
              notes: '',
              subscriptionTypeID,
              subTermID,
              termID: termStatus.termID,
              userID: user.id,
            },
            termStatusID: termStatus.id ?? '',
            userID: user.id,
          }),
        ),
      )
    } finally {
      invalidateUserTermStatuses(user.id)
      invalidateAllListingSelections(user.id)
      resetAllOrderEstimates(user.id)
    }
  }

  return { updateTermStatusesToNewDefaults }
}
